import React, { FC, useState, useEffect, useRef, Suspense } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { Environment } from '@react-three/drei';
import { ErrorBoundary } from './ErrorBoundary';
import * as THREE from 'three';
import { Pose, CameraState } from '../types';
import { PrimitiveMannequin } from './PrimitiveMannequin';

interface ThumbnailViewProps {
  pose: Pose;
  cameraState?: CameraState;
  scaleFactor?: number;
}
const ScreenshotCapture: FC<{ onCapture: (url: string) => void }> = ({ onCapture }) => {
  const { gl, scene, camera } = useThree();
  useEffect(() => {
    let animationFrameId: number | null = null;

    // Stagger snapshot captures to prevent sequential, back-to-back GPU read-backs in the same frame.
    // Spreading calls across a 50ms - 250ms window allows the GPU rendering queue to breathe.
    const captureTimer = setTimeout(() => {
      animationFrameId = requestAnimationFrame(() => {
        gl.render(scene, camera);
        onCapture(gl.domElement.toDataURL());
      });
    }, Math.random() * 200 + 50);

    return () => {
      clearTimeout(captureTimer);
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [gl, scene, camera, onCapture]);
  return null;
};

const CameraSetup: FC<{ cameraState?: CameraState; scaleFactor: number; mannequinRef: React.RefObject<THREE.Object3D | null> }> = ({ cameraState, scaleFactor, mannequinRef }) => {
  const { camera, size } = useThree();

  useEffect(() => {
    // Standardize zoom relative to height to ensure consistent framing regardless of container size.
    // We want approximately 14 world units of height to be visible.
    const visibleWorldHeight = 14;
    const baseZoom = size.height / visibleWorldHeight;

    // Center the lookAt target on the mannequin's actual vertical midpoint rather than the
    // world origin, since the rig's feet aren't exactly at y=0 — keeps the head/feet framed
    // evenly regardless of pose or zoom level.
    let targetY = 0;
    if (mannequinRef.current) {
      try {
        const box = new THREE.Box3().setFromObject(mannequinRef.current);
        if (!box.isEmpty()) {
          targetY = (box.min.y + box.max.y) / 2;
        }
      } catch {
        // mannequinRef may point to a non-Three.js stand-in in tests
      }
    }

    if (cameraState) {
      const pos = new THREE.Vector3(...cameraState.position);
      const target = new THREE.Vector3(...cameraState.target);
      const orbitDir = pos.clone().sub(target).normalize();
      const orbitDist = pos.distanceTo(target);

      const newPos = orbitDir.multiplyScalar(orbitDist);

      camera.position.set(newPos.x, newPos.y + targetY, newPos.z);
      camera.lookAt(0, targetY, 0);

      if ((camera as any).isOrthographicCamera) {
        // Apply the user's camera zoom on top of our standardized base
        (camera as any).zoom = cameraState.zoom * baseZoom * scaleFactor;
      }
    } else {
      // Default view logic
      camera.position.set(10, 8 + targetY, 10);
      camera.lookAt(0, targetY, 0);
      if ((camera as any).isOrthographicCamera) {
        (camera as any).zoom = baseZoom * scaleFactor;
      }
    }
    camera.updateProjectionMatrix();
  }, [camera, size, cameraState, scaleFactor, mannequinRef]);

  return null;
};

export const ThumbnailView: FC<ThumbnailViewProps> = React.memo(({ pose, cameraState, scaleFactor = 1 }) => {
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const mannequinRef = useRef<THREE.Group>(null);

  useEffect(() => {
    setImgUrl(null);
  }, [pose, cameraState]);

  if (imgUrl) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', pointerEvents: 'none' }}>
        <img src={imgUrl} style={{ width: '100%', height: '100%', objectFit: 'contain', userSelect: 'none' }} draggable="false" alt="pose thumbnail" />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', pointerEvents: 'none' }}>
      <Canvas 
        orthographic={true}
        camera={{
          near: -100,
          far: 100
        }} 
        gl={{ antialias: true, preserveDrawingBuffer: true, alpha: true }}
        frameloop="demand"
      >
        <color attach="background" args={['#0f172a']} />
        <ambientLight intensity={0.6} />
        <directionalLight position={[5, 10, 5]} intensity={1.5} />
        <Suspense fallback={null}>
          <ErrorBoundary name="ThumbnailEnvironment">
            <Environment files="/textures/potsdamer_platz_1k.hdr" />
          </ErrorBoundary>
        </Suspense>
        
        <CameraSetup cameraState={cameraState} scaleFactor={scaleFactor} mannequinRef={mannequinRef} />
        
        {/* Audience Direction Arrow (Premium Glass / Static / Elevated) - Outside mannequin group to remain static */}
        <group position={[0, 4.75, -2]} rotation={[Math.PI / 2, 0, 0]} scale={0.35}>
          <mesh>
            <extrudeGeometry args={[
              (() => {
                const s = new THREE.Shape();
                s.moveTo(0, 0.8);      // Tip
                s.lineTo(0.6, -0.4);   // Right Wing
                s.lineTo(0, -0.1);     // Inner Notch
                s.lineTo(-0.6, -0.4);  // Left Wing
                s.lineTo(0, 0.8);      // Back to Tip
                return s;
              })(),
              { depth: 0.15, bevelEnabled: true, bevelThickness: 0.05, bevelSize: 0.05, bevelSegments: 3 }
            ]} />
            <meshStandardMaterial 
              color="#38bdf8" 
              transparent 
              opacity={0.3} 
              emissive="#38bdf8" 
              emissiveIntensity={0.5}
              metalness={0.9}
              roughness={0.1}
            />
          </mesh>
        </group>

        <group
          ref={mannequinRef}
          rotation={[0, pose.orientation?.[1] || 0, 0]}
          scale={0.5}
          position={[
            0,
            -3.8 + (pose.elevation?.[1] || 0),
            0
          ]}
        >
          <PrimitiveMannequin pose={pose} />

          {/* Static Orbs for Left/Right distinction */}
          {/* Red Orb on Mannequin's Left (+X) */}
          <mesh position={[8, 11, 0]}>
            <sphereGeometry args={[0.2, 16, 16]} />
            <meshStandardMaterial 
              color="#ef4444" 
              emissive="#ef4444" 
              emissiveIntensity={0.5} 
              transparent
              opacity={0.3}
            />
          </mesh>

          {/* Green Orb on Mannequin's Right (-X) */}
          <mesh position={[-8, 11, 0]}>
            <sphereGeometry args={[0.2, 16, 16]} />
            <meshStandardMaterial 
              color="#22c55e" 
              emissive="#22c55e" 
              emissiveIntensity={0.5} 
              transparent
              opacity={0.3}
            />
          </mesh>
        </group>

        <ScreenshotCapture onCapture={setImgUrl} />
      </Canvas>
    </div>
  );
});
